/**
 * Runtime backend selection for the shared Firebase site.
 *
 * `demo.agentlinc.ai` and `dev.agentlinc.ai` are served by the SAME Firebase
 * site (`gtech-llmrag-dev`), so the backend cannot be chosen at build time via
 * Vite env vars alone. When the app is loaded from `demo.agentlinc.ai` we route
 * to the demo (`-stg`) backend; every other host keeps the build-time env values
 * (dev / prod behaviour is unchanged).
 */

const DEMO_HOSTNAME = 'demo.agentlinc.ai';
const DEV_HOSTNAME = 'dev.agentlinc.ai';

/**
 * On dev, the LangGraph/chat backend is served SAME-SITE at
 * `api-dev.agentlinc.ai/llm` (a path on the dev load balancer that forwards to
 * the dev LangGraph Cloud Run service). Routing chat through the agentlinc.ai
 * domain means the browser sends the `auth_token` login cookie to it, which is
 * required to enforce authentication and close the open-LLM-relay abuse vector
 * (PBU-926). Only `dev.agentlinc.ai` is affected here — demo uses DEMO_BACKEND,
 * prod keeps its build-time value.
 */
const DEV_LLM_RAG_URL = 'https://api-dev.agentlinc.ai/llm';

/**
 * Backend targets for the demo (`-stg`) tier. The Django backend is reached
 * through the api-dev load balancer's `/demo` path route (which forwards to the
 * `gtech-llmrag-vm-backend-demo` VM); LangGraph and data-ingestion are the
 * dedicated `-stg` Cloud Run services.
 */
const DEMO_BACKEND = {
  /** Django backend — core API, auth, permissions, and uploads share this. */
  api: 'https://api-dev.agentlinc.ai/demo',
  llmRag:
    'https://botunify-langgraph-backend-stg-967514985120.asia-southeast1.run.app',
  dataIngestion:
    'https://botunify-dataingestion-stg-967514985120.asia-southeast1.run.app',
} as const;

/** True when the app is running on the demo host. */
export function isDemoHost(): boolean {
  return (
    typeof window !== 'undefined' && window.location.hostname === DEMO_HOSTNAME
  );
}

/** Core Django backend base URL (auth, permissions, assistants, …). */
export function resolveBackendApiUrl(buildTimeValue: string): string {
  return isDemoHost() ? DEMO_BACKEND.api : buildTimeValue;
}

/** LangGraph / LLM-RAG backend base URL. */
export function resolveLlmRagApiUrl(buildTimeValue: string): string {
  if (isDemoHost()) return DEMO_BACKEND.llmRag;
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === DEV_HOSTNAME
  ) {
    return DEV_LLM_RAG_URL;
  }
  return buildTimeValue;
}

/** Data-ingestion backend base URL. */
export function resolveDataIngestionApiUrl(buildTimeValue: string): string {
  return isDemoHost() ? DEMO_BACKEND.dataIngestion : buildTimeValue;
}
